import { guardado } from '../lib/armazenamento';
import React, { useEffect, useState } from 'react';
import { KanbanCard } from '../features/leads/KanbanCard';
import { CAMPOS_CARD, CARD_PADRAO, STAGES } from '../lib/constantes';
import { baixarCSV, leadParaLinha } from '../lib/exportar';
import { exportarFechadosXLSX, exportarXLSX } from '../lib/xlsx';
import { ImportCSVModal } from '../features/leads/ImportCSVModal';
import { calcContractTotal, fmt } from '../lib/formato';
import { statusEsfriando } from '../lib/pipeline';
import { lerCamposCard } from '../lib/prefs';

function KanbanView({ leads, partners, sellers, company = '', camposCustom = [], onStageChange, onOpenLead, onTemperatureChange, onAddInteraction, onClearDate, onSnoozeDate, onImportLeads, isAdmin, userEmail, accentConvBar='bg-blue-400' }) {
  const [showImport, setShowImport] = useState(false);
  // C-15: filtros persistem entre sessões, um jogo por empresa. Vendedor e parceiro
  // são ids da empresa; salvar sem separar deixava um filtro invisível ao trocar de empresa.
  const CHAVE_FILTROS = company ? `kanbanFiltros:${company}` : 'kanbanFiltros';
  const salvos = (() => {
    return guardado.lerJSON(CHAVE_FILTROS, {});
  })();
  const [search, setSearch]             = useState('');
  const [partnerFilter, setPartnerFilter] = useState(salvos.partner || '');
  const [sellerFilter, setSellerFilter]   = useState(salvos.seller || '');
  const [myLeadsOnly, setMyLeadsOnly]     = useState(!!salvos.meus);
  const [esfriandoOnly, setEsfriandoOnly] = useState(!!salvos.esfriando);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [camposCard, setCamposCard] = useState(lerCamposCard);
  const [showCampos, setShowCampos] = useState(false);
  useEffect(() => {
    guardado.gravarJSON('camposCard', camposCard);
  }, [camposCard]);

  useEffect(() => {
    guardado.gravarJSON(CHAVE_FILTROS, {
      partner: partnerFilter, seller: sellerFilter,
      meus: myLeadsOnly, esfriando: esfriandoOnly,
    });
  }, [partnerFilter, sellerFilter, myLeadsOnly, esfriandoOnly]);

  // Id que não existe nesta empresa não filtra nada (e não conta como filtro ativo)
  const sellerValido  = sellers?.some(s => s.id === sellerFilter)  ? sellerFilter  : '';
  const partnerValido = partners?.some(p => p.id === partnerFilter) ? partnerFilter : '';
  const filtrosAtivos = [partnerValido, sellerValido, myLeadsOnly, esfriandoOnly].filter(Boolean).length;
  const limparFiltros = () => {
    setSearch(''); setPartnerFilter(''); setSellerFilter('');
    setMyLeadsOnly(false); setEsfriandoOnly(false);
  };
  const handleDrop = (e, stage) => { if (!isAdmin) return; e.preventDefault(); setDragOverStage(null); onStageChange(e.dataTransfer.getData('leadId'), stage); };
  const handleDragOver = (e, stage) => { if (!isAdmin) return; e.preventDefault(); setDragOverStage(stage); };
  const handleDragLeave = (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverStage(null); };
  const today = new Date(); today.setHours(0,0,0,0);
  const overdueLeads = leads.filter(l => l.next_contact_date && !['fechado','perdido'].includes(l.stage) && new Date(l.next_contact_date+'T00:00:00') < today);
  const todayLeads   = leads.filter(l => l.next_contact_date && !['fechado','perdido'].includes(l.stage) && new Date(l.next_contact_date+'T00:00:00').getTime()===today.getTime());
  // Vendedor que corresponde ao usuário logado
  const mySeller = sellers?.find(s => s.email?.toLowerCase().trim() === userEmail?.toLowerCase().trim());

  const visibleLeads = leads.filter(l => {
    // Lead sem vendedor ainda não é de ninguém, então é de todo mundo: passa por qualquer filtro de vendedor.
    if (myLeadsOnly && mySeller && l.seller_id && l.seller_id !== mySeller.id) return false;
    if (search && !l.company_name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (partnerValido && !l.partners?.some(p => p.id === partnerValido)) return false;
    if (sellerValido && l.seller_id && l.seller_id !== sellerValido) return false;
    if (esfriandoOnly && !statusEsfriando(l)) return false;
    return true;
  });

  return <div className="flex flex-col gap-4">
    {/* Filtros do Kanban */}
    <div className="flex gap-2 flex-wrap">
      {mySeller && <button
        onClick={()=>setMyLeadsOnly(v=>!v)}
        className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-full font-semibold border transition-all whitespace-nowrap ${myLeadsOnly ? 'bg-blue-500 text-white border-transparent shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
        👤 Meus Leads{myLeadsOnly && <span className="bg-white/20 rounded-full text-xs px-1.5">{visibleLeads.filter(l=>l.seller_id===mySeller.id).length}</span>}
      </button>}
      <button
        onClick={()=>setEsfriandoOnly(v=>!v)}
        aria-pressed={esfriandoOnly}
        title="Mostrar só os parados além do limite da etapa"
        className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-full font-semibold border transition-all whitespace-nowrap ${esfriandoOnly ? 'bg-amber-500 text-white border-transparent shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
        <span aria-hidden="true">⏳</span> Esfriando
        {(() => { const n = leads.filter(l=>statusEsfriando(l)).length;
          return n > 0 ? <span className={`rounded-full text-xs px-1.5 ${esfriandoOnly ? 'bg-white/20' : 'bg-amber-100 text-amber-700'}`}>{n}</span> : null; })()}
      </button>
      <input
        className="apple-input flex-1 min-w-48 px-3 py-2 text-sm"
        placeholder="🔍 Buscar empresa..."
        value={search} onChange={e=>setSearch(e.target.value)}
      />
      <select className="apple-input px-3 py-2 text-sm"
        value={partnerValido} onChange={e=>setPartnerFilter(e.target.value)}>
        <option value="">Todos os parceiros</option>
        {partners.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {sellers?.length>0 && <select className="apple-input px-3 py-2 text-sm"
        value={sellerValido} onChange={e=>setSellerFilter(e.target.value)}>
        <option value="">Todos os vendedores</option>
        {sellers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
      </select>}
      <div className="relative">
        <button onClick={()=>setShowCampos(v=>!v)}
          aria-expanded={showCampos} aria-label="Personalizar campos do cartão"
          title="Escolher o que aparece no cartão"
          className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-full font-medium border transition-colors whitespace-nowrap ${showCampos ? 'bg-gray-900 text-white border-transparent' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
          <span aria-hidden="true">⚙</span> Cartão
        </button>
        {showCampos && <>
          <div className="fixed inset-0 z-30" onClick={()=>setShowCampos(false)} />
          <div className="absolute right-0 mt-2 w-60 acard rounded-2xl shadow-lg p-3 z-40">
            <p className="text-xs font-semibold text-gray-700 mb-2">Mostrar no cartão</p>
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {CAMPOS_CARD.map(c=>(
                <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-black/[0.04] cursor-pointer">
                  <input type="checkbox" checked={!!camposCard[c.key]}
                    onChange={e=>setCamposCard(v=>({...v,[c.key]:e.target.checked}))} />
                  <span className="text-sm text-gray-700">{c.label}</span>
                </label>
              ))}
            </div>
            <button onClick={()=>setCamposCard({...CARD_PADRAO})}
              className="mt-2 w-full text-xs text-gray-400 hover:text-gray-600 py-1.5 transition-colors">
              Restaurar padrão
            </button>
          </div>
        </>}
      </div>
      {filtrosAtivos > 0 && <button onClick={limparFiltros}
        className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-full font-medium border border-gray-200 bg-white text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors whitespace-nowrap">
        <span aria-hidden="true">✕</span> Limpar ({filtrosAtivos})
      </button>}
      <button onClick={()=>baixarCSV('leads-visiveis', visibleLeads.map(l=>leadParaLinha(l, camposCustom)))}
        title="Exporta os leads que estão visíveis com os filtros atuais"
        className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-full font-medium border border-gray-200 bg-white text-gray-600 hover:border-gray-300 transition-colors whitespace-nowrap">
        <span aria-hidden="true">⬇</span> CSV ({visibleLeads.length})
      </button>
      <button onClick={()=>exportarXLSX('leads-visiveis', [{ nome: 'Leads', linhas: visibleLeads.map(l=>leadParaLinha(l, camposCustom)), moeda: ['Valor mensal', 'Valor contrato'] }])}
        title="Planilha .xlsx formatada com os leads visíveis"
        className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-full font-medium border border-gray-200 bg-white text-gray-600 hover:border-gray-300 transition-colors whitespace-nowrap">
        <span aria-hidden="true">⬇</span> XLSX
      </button>
      {isAdmin && onImportLeads && <button onClick={()=>setShowImport(true)}
        title="Importar leads de uma planilha .csv"
        className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-full font-medium border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors whitespace-nowrap">
        <span aria-hidden="true">⬆</span> Importar CSV
      </button>}
      <button onClick={()=>exportarFechadosXLSX(leads)}
        className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2 rounded-full font-semibold shadow-sm whitespace-nowrap transition-colors">
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
        ⬇ Fechados
      </button>
    </div>
    {showImport && <ImportCSVModal leads={leads} sellers={sellers} onImport={onImportLeads} onClose={()=>setShowImport(false)} />}
    {(overdueLeads.length>0||todayLeads.length>0) && <div className="flex gap-3 flex-wrap">
      {overdueLeads.length>0 && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2 flex items-center gap-2">
        <span className="text-sm font-semibold text-red-700">{overdueLeads.length} atrasado{overdueLeads.length>1?'s':''}</span>
      </div>}
      {todayLeads.length>0 && <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-2 flex items-center gap-2">
        <span className="text-sm font-semibold text-orange-700">{todayLeads.length} abordagem hoje</span>
      </div>}
    </div>}
    {(()=>{
      const FUNNEL_ORDER = ['backlog','em_contato','qualificacao','aguardando','proposta','criando_proposta','negociacao','fechado'];
      const foraDoFunil = l => l.stage === 'perdido' || l.stage === 'repescagem';
      const totalActive = visibleLeads.filter(l => !foraDoFunil(l)).length;
      return <div className="flex gap-3 overflow-x-auto pb-2">
        {STAGES.map(stage => {
          const stageLeads = visibleLeads.filter(l=> stage.key==='backlog' ? (l.stage==='backlog'||l.stage==='prospeccao'||!l.stage) : l.stage===stage.key);
          const stageValue = stageLeads.reduce((s,l)=>s+(Number(l.value)||0),0);
          const stageTotalContract = stageLeads.reduce((s,l)=>s+calcContractTotal(l.value,l.recurrence),0);
          const isClosed = stage.key === 'fechado';
          const isLost   = stage.key === 'perdido';
          const isRepesca = stage.key === 'repescagem';
          // Funil cumulativo: % de leads ativos que chegaram a esta etapa ou além
          const funnelIdx = FUNNEL_ORDER.indexOf(stage.key);
          let convRate = null;
          if (!isLost && !isRepesca && funnelIdx >= 0 && totalActive > 0) {
            const leadsAtOrAfter = visibleLeads.filter(l => {
              if (foraDoFunil(l)) return false;
              const li = FUNNEL_ORDER.indexOf((!l.stage || l.stage==='prospeccao') ? 'backlog' : l.stage);
              return li >= funnelIdx;
            }).length;
            convRate = Math.round(leadsAtOrAfter / totalActive * 100);
          }
          const isDragTarget = isAdmin && dragOverStage === stage.key;
          const colBg    = isDragTarget ? 'bg-blue-50 ring-2 ring-blue-400 ring-dashed' : isClosed ? 'bg-green-50 ring-2 ring-green-300 ring-inset' : isLost ? 'bg-red-50/70' : isRepesca ? 'bg-indigo-50/70 ring-2 ring-indigo-200 ring-inset' : 'bg-black/[0.035]';
          const lblColor = isClosed ? 'text-green-700' : isLost ? 'text-red-500' : isRepesca ? 'text-indigo-600' : 'text-gray-500';
          const cntBg    = isClosed ? 'bg-green-500 text-white' : isLost ? 'bg-red-100 text-red-600' : isRepesca ? 'bg-indigo-500 text-white' : 'bg-white/80 text-gray-600';
          return <div key={stage.key}
            onDragOver={e=>handleDragOver(e,stage.key)}
            onDragLeave={handleDragLeave}
            onDrop={e=>handleDrop(e,stage.key)}
            className={`flex-shrink-0 w-64 rounded-2xl transition-all ${colBg} flex flex-col`}>
            <div className="px-3 py-2.5">
              <div className="flex items-center justify-between">
                <span className={`t-caption-2 font-semibold sf-caption ${lblColor}`}>{stage.label}</span>
                <span className={`text-xs font-semibold rounded-full px-2 py-0.5 shadow-sm ${cntBg}`}>{stageLeads.length}</span>
              </div>
              {stageValue>0 && <div className={`text-xs font-medium mt-0.5 tabular ${isClosed ? 'text-green-600 font-semibold' : 'text-gray-400'}`}>
                {fmt(stageValue)}{stageLeads.some(l=>l.recurrence && l.recurrence!=='unica') ? <span className="opacity-60">/mês</span> : ''}
              </div>}
              {stageTotalContract > stageValue && <div className={`text-xs font-bold mt-0.5 tabular ${isClosed ? 'text-emerald-700' : 'text-gray-500'}`}>
                {fmt(stageTotalContract)} <span className="font-normal opacity-70">total contrato</span>
              </div>}
              {convRate !== null && (
                <div className="flex items-center gap-1.5 mt-1.5">
                  <div className="h-1 rounded-full flex-1 bg-gray-200/70 overflow-hidden">
                    <div className={`h-1 rounded-full transition-all ${isClosed ? 'bg-green-500' : accentConvBar}`} style={{width:`${convRate}%`}}></div>
                  </div>
                  <span className={`t-caption-2 font-bold tabular-nums ${isClosed ? 'text-green-600' : 'text-gray-400'}`}>{convRate}%</span>
                </div>
              )}
            </div>
            <div className="p-2 flex flex-col gap-2 flex-1 overflow-y-auto min-h-[200px]">
              {stageLeads.map(lead=><KanbanCard key={lead.id} lead={lead} campos={camposCard} company={company} camposCustom={camposCustom} onClick={()=>onOpenLead(lead)} draggable={isAdmin} onTemperatureChange={isAdmin ? onTemperatureChange : null} onAddInteraction={isAdmin ? onAddInteraction : null} onClearDate={isAdmin ? onClearDate : null} onSnoozeDate={isAdmin ? onSnoozeDate : null} />)}
            </div>
          </div>;
        })}
      </div>;
    })()}
  </div>;
}

// ─── ListView ─────────────────────────────────────────────

export { KanbanView };
